import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, Send, Check } from "lucide-react";
import { supabase, getAuthHeader } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface Message {
  role: "user" | "assistant";
  text: string;
  updatedJson?: string | null;
  basedOnJson?: string;
  applied?: boolean;
}

function summarizeDiff(before: string, after: string): string {
  const a = before.split("\n").length;
  const b = after.split("\n").length;
  const sizeDelta = after.length - before.length;
  const lineDelta = b - a;
  const sign = (n: number) => (n >= 0 ? `+${n}` : `${n}`);
  return `${sign(lineDelta)} lines · ${sign(sizeDelta)} chars`;
}

interface WizardAssistantProps {
  configType: "brief" | "product" | "photographer";
  currentJson: string;
  onApplyJson: (json: string) => void | Promise<void>;
}

export function WizardAssistant({ configType, currentJson, onApplyJson }: WizardAssistantProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const prompt = input.trim();
    if (!prompt || loading) return;
    setError(null);
    setMessages((prev) => [...prev, { role: "user", text: prompt }]);
    setInput("");
    setLoading(true);

    try {
      const authHeader = await getAuthHeader();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/wizard-assistant`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: authHeader },
          body: JSON.stringify({ configType, currentJson, prompt }),
        },
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Request failed: ${res.status}`);
      }

      const data = (await res.json()) as { reply: string; updated_json?: string | null };
      setMessages((prev) => [...prev, {
        role: "assistant",
        text: data.reply,
        updatedJson: data.updated_json || null,
        basedOnJson: currentJson,
        applied: false,
      }]);
    } catch (err: any) {
      setError(err.message || "Failed to call assistant");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-border shrink-0">
        <Sparkles className="w-3 h-3 text-primary" />
        <span className="text-[11px] font-medium text-foreground">Assistant</span>
      </div>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto p-3 space-y-3">
        {messages.map((m, i) => (
          <div
            key={i}
            className={cn(
              "rounded-lg p-2.5 text-xs leading-relaxed",
              m.role === "user" ? "bg-primary/10 border border-primary/20" : "bg-muted/50 border border-border",
            )}
          >
            <div className="whitespace-pre-wrap text-foreground">{m.text}</div>
            {m.updatedJson && (
              <div className="mt-2 pt-2 border-t border-border flex items-center justify-between gap-2">
                <span className="text-[10px] text-muted-foreground">
                  {summarizeDiff(m.basedOnJson || "", m.updatedJson)}
                </span>
                {m.applied ? (
                  <span className="flex items-center gap-1 text-[11px] text-green-600 font-medium">
                    <Check className="w-3 h-3" /> Applied
                  </span>
                ) : (
                  <Button
                    size="sm"
                    variant="default"
                    className="h-6 text-[11px] px-2"
                    onClick={async () => {
                      await onApplyJson(m.updatedJson!);
                      setMessages((prev) => prev.map((msg, idx) => (idx === i ? { ...msg, applied: true } : msg)));
                    }}
                  >
                    Apply
                  </Button>
                )}
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="rounded-lg p-3 bg-muted/50 border border-border flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="w-3 h-3 animate-spin" /> Thinking…
          </div>
        )}
        {error && (
          <div className="rounded-lg p-3 border border-destructive/50 bg-destructive/5 text-xs text-destructive">
            {error}
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-border p-2 flex items-end gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={2}
          placeholder="Ask a question or describe a change…"
          className="flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
          disabled={loading}
        />
        <Button size="sm" onClick={send} disabled={loading || !input.trim()}>
          <Send className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
